import { ref, computed, watch, nextTick } from 'vue';
import { defineStore } from 'pinia';
import { provideApolloClient, useQuery } from '@vue/apollo-composable';
import { IFormStep } from '@/interfaces';
import { JP_EXCEPTION } from '@/constants/jeopardy-codes.const';
import { useFormStore, useAnalyticsStore, useConfigSettingsStore, SettingCategory } from '@/store';
import { FormEvents } from '@/enums/form-events.enum';
import jpCodeQueryGraphQL from './jeopardy-code.query.graphql';
import jpPanelQueryGraphQL from './jeopardy-panel.query.graphql';
import { Field } from '@sitecore-jss/sitecore-jss-vue';
import GraphQLClientFactory from '@/lib/GraphQLClientFactory';
import config from '@/temp/config';
import { IAnalyticsForm, IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { trimHTML } from '@/utils/data-transforms';

interface JPPanelQueryResult {
  item: {
    id: string;
    name: string;
    jeopardyPanelCode: {
      value: string;
    };
    liveChatHeading: {
      value: string;
    };
    liveChatContent: {
      value: string;
    };
    liveChatButtonText: {
      value: string;
    };
    callUsHeading: {
      value: string;
    };
    callUsContent: {
      value: string;
    };
    callUsNumber: {
      value: string;
    };
    callUsButtonLabel: {
      value: string;
    };
    callBackHeading: {
      value: string;
    };
    callBackContent: {
      value: string;
    };
    callBackButtonText: {
      value: string;
    };
    callBackFormLink: {
      value: string;
    };
  };
}

export const useJeopardyStore = defineStore('FormJeopardy', () => {
  const formStore = useFormStore();
  const analyticsStore = useAnalyticsStore();
  const configStore = useConfigSettingsStore();

  const step = ref<IFormStep>({
    stepNumber: -1,
    stepName: 'Jeopardy',
    stepTitle: '',
  });
  const showJeopardy = ref(false);
  const showDynamic = ref(false);
  const isChatEnabled = ref(false);
  const isChatLoading = ref(false);
  const isCallBackVisible = ref(false);
  const isCallUsVisible = ref(true);
  const jpCode = ref(JP_EXCEPTION);
  const additionalComments = ref();
  const cancellationReasons = ref();
  const backBtnDisabled = ref(false);
  const backBtnCallback = ref<() => void>();
  const backBtnText = ref('Back');
  const jpTextSuffix = ref<string>('');
  const formAnalyticsOverride = ref<Partial<IAnalyticsForm> | null>(null);
  const jpDynamicPanelResult = ref<JPPanelQueryResult | null>(null);
  const jpDefaultPanelResult = ref<JPPanelQueryResult | null>(null);
  const isInsuranceQuote = ref(false);
  const iqJeopardyContentReplacement = ref('');
  const iqJeopardyTitleReplacement = ref('');

  const jpCodeQueryGraphQLPath = computed(
    () => '/sitecore/content/RACQFormsGroup/RACQForms20/Data/Jeopardy/Jeopardy Codes/' + jpCode.value
  );
  provideApolloClient(GraphQLClientFactory(config.graphQLEndpoint, true));
  const { result: jpCodeQueryResult, loading: jpCodeQueryLoading } = useQuery(jpCodeQueryGraphQL, {
    path: jpCodeQueryGraphQLPath,
  });

  /**
   * Config from CMS
   */
  const isHard = computed(() => jpCodeQueryResult.value?.item?.isHard?.boolValue ?? false);
  const liveChatDisabled = computed(() => jpCodeQueryResult.value?.item?.liveChatDisabled?.boolValue ?? false);
  const jpContentLoaded = computed(() => !jpCodeQueryLoading.value);
  const jpHeading = computed(() => jpCodeQueryResult.value?.item?.jpHeading?.value as string);
  const enableFullWidthContent = computed(
    () => jpCodeQueryResult.value?.item?.enableFullWidthContent?.boolValue ?? false
  );

  // Replace 'change' and 'request' to 'quote' for shared jeopardy messages
  const jpStepTitle = computed(() => {
    let title = jpCodeQueryResult.value?.item?.jpStepTitle?.value as string;
    if (isInsuranceQuote.value && title) {
      title = title.replace(...(iqJeopardyTitleReplacement.value?.split(',') as [string, string]));
    }
    return title;
  });
  const jpText = computed(() => {
    let text = (jpCodeQueryResult.value?.item?.jpText?.rendered ?? '') + jpTextSuffix.value;
    if (isInsuranceQuote.value && text) {
      text = text.replace(...(iqJeopardyContentReplacement.value?.split(',') as [string, string]));
    }
    return text;
  });
  const jpErrorDescription = computed(() => {
    const description = jpCodeQueryResult.value?.item?.jpErrorDescription?.value as string;
    return description;
  });

  watch(jpStepTitle, (newValue) => {
    if (newValue) {
      step.value.stepTitle = newValue;
    }
  });

  watch(isInsuranceQuote, async (newValue) => {
    if (!newValue) return;
    iqJeopardyTitleReplacement.value = (await configStore.getSetting(
      'IQJeopardyTitleReplacement',
      SettingCategory.IQSettings
    )) as string;
    iqJeopardyContentReplacement.value = (await configStore.getSetting(
      'IQJeopardyContentReplacement',
      SettingCategory.IQSettings
    )) as string;
  });

  const jpPanelQueryLoading = ref(true);
  const fetchDynamic = async (jeopardyPanelName: string): Promise<void> => {
    const jpPanelQueryGraphQLPath = computed(
      () => '/sitecore/content/RACQFormsGroup/RACQForms20/Data/Jeopardy/Jeopardy Panels/' + jeopardyPanelName
    );
    const { result: jpPanelQueryResult, loading: jpPanelQueryLoadingTemp } = useQuery(jpPanelQueryGraphQL, {
      path: jpPanelQueryGraphQLPath,
    });

    // Watch for changes in the query result and update our ref
    watch(jpPanelQueryResult, (newValue) => {
      jpDynamicPanelResult.value = newValue;
      jpPanelQueryLoading.value = jpPanelQueryLoadingTemp.value;
    });

    // Returns when loading is done
    return new Promise((resolve) => {
      watch(
        jpPanelQueryLoading,
        (newValue) => {
          if (!newValue) {
            resolve();
          }
        },
        { immediate: true }
      );
    });
  };

  watch(
    formAnalyticsOverride,
    async (newValue, oldValue) => {
      // Need for computed properties to be ready (jpText)
      await Promise.all([nextTick(), waitForJPText()]);
      // Jp code is not null AND show jp AND jp details it not null AND jp details have changed (avoid duplicate analytics)
      if (
        jpCode.value &&
        showJeopardy.value &&
        newValue &&
        (newValue?.formErrorType !== oldValue?.formErrorType || newValue?.formErrorDetail !== oldValue?.formErrorDetail)
      ) {
        const model: IFormAnalytics = {
          event: FormEvents.JEOPARDY,
          form: {
            ...newValue,
            formErrorType: newValue.formErrorType ?? jpCode.value,
            formErrorDetail: trimHTML(newValue.formErrorDetail ?? jpText.value),
          },
        };
        if (formStore.formName) {
          model.form.name = formStore.formName;
        }
        if (!model.form.formError && jpErrorDescription?.value) {
          model.form.formError = jpErrorDescription.value;
        }
        model.form.prevStepName = model.form.prevStepName ?? formStore.currentStep?.stepName;
        analyticsStore.pushAnalytics(model);
      }
    },
    { deep: true }
  );

  /**
   * Run callback for cancel
   */
  const btnBackClick = () => {
    if (backBtnCallback.value) backBtnCallback.value();
    else {
      showJeopardy.value = false;
      formStore.unMaskActiveStep();
    }
  };

  /**
   * Set Chat Loading
   * @param value
   */
  const setChatLoading = (value: boolean) => {
    isChatLoading.value = value;
  };

  /**
   * Set Chat Enabled
   * @param value
   */
  const setChatEnabled = (value: boolean) => {
    isChatEnabled.value = value;
    isChatLoading.value = false;
  };

  /**
   * Hide Jeopardy Panel
   * @param value
   */
  const hideJeopardyPanel = () => {
    showJeopardy.value = false;
  };

  /**
   * Show JP Panel
   * @param options
   */
  const showJeopardyPanel = (options: {
    jpCode: string;
    isChatEnabled?: boolean;
    isCallBackVisible?: boolean;
    isCallUsVisible?: boolean;
    backBtnDisabled?: boolean;
    backBtnText?: string;
    backBtnCallback?: () => void;
    areNextStepsVisible?: boolean; // Default: false
    showCurrentStepDisclaimer?: boolean; // Default: true
    dynamicJeopardyPanelName?: string; // If this param is passed then the method MUST be treated as async
    textSuffix?: string;
    formAnalyticsOverride?: Partial<IAnalyticsForm> | null; // null to omit sending analytics
  }): Promise<void> | void => {
    // initialise common jeopardy config values
    const updateJeopardyConfig = () => {
      if (options.jpCode?.length && options.jpCode.startsWith('JP')) jpCode.value = options.jpCode;
      if (options.isChatEnabled !== undefined) isChatEnabled.value = options.isChatEnabled;
      if (options.isCallBackVisible !== undefined) isCallBackVisible.value = options.isCallBackVisible;
      if (options.isCallUsVisible !== undefined) isCallUsVisible.value = options.isCallUsVisible;
      if (options.backBtnDisabled !== undefined) backBtnDisabled.value = options.backBtnDisabled;
      if (options.backBtnDisabled) formStore.makeStepperVisible(false); // Stepper should be hidden when back button is disabled.
      if (options.backBtnText !== undefined) backBtnText.value = options.backBtnText;
      if (options.backBtnCallback !== undefined) backBtnCallback.value = options.backBtnCallback;
      if (options.textSuffix !== undefined) jpTextSuffix.value = options.textSuffix;
      if (options.formAnalyticsOverride !== undefined) {
        formAnalyticsOverride.value = options.formAnalyticsOverride;
      } else {
        formAnalyticsOverride.value = { formErrorType: jpCode.value };
      }

      formStore[options.areNextStepsVisible === true ? 'showNextSteps' : 'hideNextSteps']();
      formStore.isStepDisclaimerVisible = options.showCurrentStepDisclaimer ?? true;
      formStore.maskActiveStep();
    };

    if (!options.dynamicJeopardyPanelName) {
      showJeopardy.value = true;
      showDynamic.value = false;
      updateJeopardyConfig();
    } else {
      // Method is ASYNC
      // eslint-disable-next-line no-async-promise-executor
      return new Promise<void>(async (resolve) => {
        showDynamic.value = true;
        await setDynamicJeopardyPanel(options.dynamicJeopardyPanelName!);
        showJeopardy.value = true;
        updateJeopardyConfig();
        resolve();
      });
    }
  };

  const setDynamicJeopardyPanel = async (panelName: string) => {
    await fetchDynamic(panelName);
  };

  const setDefaultJeopardyPanel = (fields: Record<string, Field<any>>) => {
    jpDefaultPanelResult.value = {
      item: {
        id: '',
        name: '',
        jeopardyPanelCode: { value: fields.jeopardyPanelCode.value },
        liveChatHeading: { value: fields.liveChatHeading.value },
        liveChatContent: { value: fields.liveChatContent.value },
        liveChatButtonText: { value: fields.liveChatButtonText.value },
        callUsHeading: { value: fields.callUsHeading.value },
        callUsContent: { value: fields.callUsContent.value },
        callUsButtonLabel: { value: fields.callUsButtonLabel.value },
        callUsNumber: { value: fields.callUsNumber.value },
        callBackHeading: { value: fields.callBackHeading.value },
        callBackContent: { value: fields.callBackContent.value },
        callBackButtonText: { value: fields.callBackButtonText.value },
        callBackFormLink: { value: fields.callBackFormLink.value },
      },
    };
  };

  const waitForJPText = (maxTimeout = 2050) => {
    return new Promise((res) => {
      let completed = false;
      const timer = setInterval(() => {
        if (jpContentLoaded.value) {
          res(true);
          clearInterval(timer);
          completed = true;
        }
      }, 100);

      setTimeout(() => {
        if (!completed) {
          clearInterval(timer);
          res(true);
        }
      }, maxTimeout);
    });
  };

  return {
    step,
    showJeopardy,
    showDynamic,
    isChatEnabled,
    isChatLoading,
    isCallBackVisible,
    isCallUsVisible,
    isInsuranceQuote,
    jpDynamicPanelResult,
    jpDefaultPanelResult,
    jpCode,
    isHard,
    liveChatDisabled,
    jpText,
    jpContentLoaded,
    jpTextSuffix,
    jpHeading,
    jpStepTitle,
    jpErrorDescription,
    additionalComments,
    cancellationReasons,
    backBtnDisabled,
    backBtnCallback,
    backBtnText,
    enableFullWidthContent,
    btnBackClick,
    setChatLoading,
    setChatEnabled,
    hideJeopardyPanel,
    showJeopardyPanel,
    setDynamicJeopardyPanel,
    setDefaultJeopardyPanel,
  };
});
